/**
 * Classic Site auth API — session verification via existing hoodie endpoint only.
 *
 * Canonical session check: GET /api/hoodie/hoodies/me/
 *   - Verified in HoodieViewSet.me (hoodie/api_views.py)
 *   - Returns HoodieProfileSerializer: user, zip_code, recommendations, gravatar_url
 *   - 401/403 when not authenticated (PermissionDenied)
 *
 * Not used for session check:
 *   - GET /api/hoodie/users/{id}/ — own user only; requires knowing id first
 *   - mainApi JWT — separate auth stack
 *   - adminToolsBridgeApi — staff JWT only
 *
 * Credentials: include (session cookies required on GET)
 * CSRF: not required for GET hoodies/me/
 */

import { diagnoseSessionFetch } from "../api/classicSiteEnvironmentDiagnostics";
import { interpretSessionHttpStatus, SESSION_PROFILE_PATH } from "./sessionHelpers";

/**
 * @typedef {'authenticated'|'unauthenticated'|'error'|'unconfigured'} SessionCheckResult
 * @typedef {import('../api/classicSiteEnvironmentDiagnostics').EnvironmentFetchDiagnostic} SessionDiagnostic
 * @typedef {{ status: SessionCheckResult, profile: object|null, httpStatus: number|null, message: string|null, diagnostic: SessionDiagnostic|null }} SessionResponse
 */

/**
 * Check Django session via hoodies/me/.
 * @returns {Promise<SessionResponse>}
 */
export async function fetchSessionProfile() {
  const diagnostic = await diagnoseSessionFetch("include");

  if (diagnostic.errorCategory === "unconfigured") {
    return {
      status: "unconfigured",
      profile: null,
      httpStatus: null,
      message: diagnostic.errorMessage,
      diagnostic,
    };
  }

  if (diagnostic.ok && diagnostic.data) {
    return {
      status: "authenticated",
      profile: diagnostic.data,
      httpStatus: diagnostic.status,
      message: null,
      diagnostic,
    };
  }

  if (
    diagnostic.status === 401 ||
    diagnostic.status === 403 ||
    (diagnostic.status && interpretSessionHttpStatus(diagnostic.status) === "unauthenticated")
  ) {
    return {
      status: "unauthenticated",
      profile: null,
      httpStatus: diagnostic.status,
      message:
        diagnostic.errorMessage ||
        (diagnostic.status === 403 || diagnostic.status === 401
          ? "No Django session detected"
          : `HTTP ${diagnostic.status}`),
      diagnostic,
    };
  }

  return {
    status: "error",
    profile: null,
    httpStatus: diagnostic.status,
    message: diagnostic.errorMessage || "Session check failed",
    diagnostic,
  };
}

export { SESSION_PROFILE_PATH };
